import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Plug, ShieldCheck, Trash2, Play, Save, ArrowLeftRight,
  Info, Clock, CheckCircle2, XCircle, AlertTriangle, ListChecks,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
axios.defaults.withCredentials = true;

const ENTITY_LABELS = {
  customers: "Müşteriler / Cariler",
  suppliers: "Tedarikçiler",
  products: "Ürünler",
  stock: "Stoklar",
  orders: "Siparişler",
  invoices: "Faturalar",
  payments: "Tahsilat / Ödeme",
};

const INTERVAL_LABELS = {
  manual: "Manuel",
  "15m": "Her 15 dk",
  "30m": "Her 30 dk",
  hourly: "Saatlik",
  daily: "Günlük",
};

export default function AccountingHub() {
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // provider id chosen for config
  const [configForm, setConfigForm] = useState({});
  const [schedule, setSchedule] = useState({ sync_interval: "manual", entities: [] });
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [mapping, setMapping] = useState({ defaults: {}, overrides: {} });
  const [testResult, setTestResult] = useState(null);
  const [showMapping, setShowMapping] = useState(null); // {entity, mapping}
  const [saving, setSaving] = useState(false);
  const [syncingEntity, setSyncingEntity] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pr, cf, lg, qz, mp] = await Promise.all([
        axios.get(`${API}/accounting/providers`),
        axios.get(`${API}/accounting/config`),
        axios.get(`${API}/accounting/sync-log?limit=50`),
        axios.get(`${API}/accounting/queue`),
        axios.get(`${API}/accounting/mapping`).catch(() => ({ data: { defaults: {}, overrides: {} } })),
      ]);
      setProviders(pr.data.items || []);
      setConfig(cf.data && cf.data.provider_id ? cf.data : null);
      setLogs(lg.data.items || []);
      setQueue(qz.data.items || []);
      setMapping(mp.data || { defaults: {}, overrides: {} });
      if (cf.data?.sync_interval) {
        setSchedule({
          sync_interval: cf.data.sync_interval,
          entities: cf.data.auto_sync_entities || [],
        });
      }
    } catch (e) {
      toast.error("Muhasebe verisi yüklenemedi: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const activeProviderMeta = providers.find(p => p.id === (config?.provider_id));

  const openConfig = (prov) => {
    setSelected(prov);
    const empty = {};
    (prov.default_config_schema?.fields || []).forEach(f => {
      empty[f.key] = f.default || "";
    });
    setConfigForm(empty);
    setTestResult(null);
  };

  const saveConfig = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/accounting/config`, {
        provider_id: selected.id,
        config: configForm,
        active: true,
      });
      setConfig(r.data);
      toast.success(`${selected.name} bağlandı`);
      setSelected(null);
      await loadAll();
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!config) return;
    setTestResult({ loading: true });
    try {
      const r = await axios.post(`${API}/accounting/test`);
      setTestResult(r.data);
      if (r.data.ok) toast.success("Bağlantı başarılı");
      else toast.error("Bağlantı başarısız: " + r.data.message);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setTestResult({ ok: false, message: msg });
      toast.error("Test başarısız: " + msg);
    }
  };

  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const runPreview = async () => {
    if (!config) return;
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const r = await axios.post(`${API}/accounting/preview`);
      setPreviewData(r.data);
      if (r.data.ok) toast.success("Önizleme hazır (ERP'ye yazılmadı)");
      else toast.warning("Önizleme tamamlandı, bazı uçlarda hata var");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("Önizleme başarısız: " + msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Muhasebe bağlantısı kaldırılsın mı? Yerel veriler korunacak.")) return;
    try {
      await axios.post(`${API}/accounting/disconnect`);
      toast.success("Bağlantı kesildi");
      setConfig(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const syncOne = async (entity) => {
    setSyncingEntity(entity);
    try {
      const r = await axios.post(`${API}/accounting/sync/${entity}`);
      if (r.data.ok) {
        toast.success(
          `${ENTITY_LABELS[entity]}: ${r.data.upserted} kayıt (${r.data.fetched} çekildi)`,
        );
      } else {
        toast.error(`${ENTITY_LABELS[entity]} senkron başarısız: ${r.data.errors?.[0]}`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSyncingEntity(null);
    }
  };

  const saveSchedule = async () => {
    try {
      await axios.post(`${API}/accounting/schedule`, schedule);
      toast.success("Sync zamanlama kaydedildi");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const retryQueue = async (id) => {
    try {
      await axios.post(`${API}/accounting/queue/${id}/retry`);
      toast.success("Retry başlatıldı");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const saveMapping = async (entity, m) => {
    try {
      await axios.put(`${API}/accounting/mapping/${entity}`, { mapping: m });
      toast.success("Alan eşleştirme kaydedildi");
      await loadAll();
      setShowMapping(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-slate-400 flex items-center gap-2" data-testid="accounting-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Yükleniyor…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="accounting-hub">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
            <Plug className="w-6 h-6 text-emerald-400" />
            Muhasebe Entegrasyon Merkezi
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Türkiye'de yaygın muhasebe programlarıyla iki-yönlü senkronizasyon.
            Her tenant kendi sağlayıcısını ve kimlik bilgilerini yönetir.
            Kimlik bilgileri şifreli saklanır ve arayüzde asla açık gösterilmez.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadAll}
          data-testid="accounting-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Yenile
        </Button>
      </div>

      <Tabs defaultValue="providers" className="space-y-4">
        <TabsList className="bg-slate-900/60">
          <TabsTrigger value="providers" data-testid="tab-providers">Sağlayıcılar</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status" disabled={!config}>Durum</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-mapping" disabled={!config}>Alan Eşleştirme</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Log & Kuyruk</TabsTrigger>
        </TabsList>

        {/* -------- PROVIDERS TAB -------- */}
        <TabsContent value="providers" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map(p => (
              <ProviderCard
                key={p.id}
                provider={p}
                active={config?.provider_id === p.id}
                onConfigure={() => openConfig(p)}
                onDisconnect={disconnect}
              />
            ))}
          </div>
        </TabsContent>

        {/* -------- STATUS TAB -------- */}
        <TabsContent value="status" className="space-y-4">
          {config && (
            <StatusPanel
              config={config}
              activeMeta={activeProviderMeta}
              schedule={schedule}
              setSchedule={setSchedule}
              saveSchedule={saveSchedule}
              syncOne={syncOne}
              syncingEntity={syncingEntity}
              testConnection={testConnection}
              testResult={testResult}
              disconnect={disconnect}
              runPreview={runPreview}
              previewData={previewData}
              previewLoading={previewLoading}
            />
          )}
        </TabsContent>

        {/* -------- MAPPING TAB -------- */}
        <TabsContent value="mapping" className="space-y-4">
          <MappingPanel mapping={mapping} activeMeta={activeProviderMeta}
                        onEdit={(entity, m) => setShowMapping({ entity, mapping: m })} />
        </TabsContent>

        {/* -------- LOGS TAB -------- */}
        <TabsContent value="logs" className="space-y-4">
          <LogsPanel logs={logs} queue={queue} onRetry={retryQueue} />
        </TabsContent>
      </Tabs>

      {/* Config dialog */}
      {selected && (
        <ConfigDialog
          provider={selected}
          form={configForm}
          setForm={setConfigForm}
          onClose={() => setSelected(null)}
          onSave={saveConfig}
          saving={saving}
          existing={config?.provider_id === selected.id ? config : null}
        />
      )}

      {/* Mapping edit dialog */}
      {showMapping && (
        <MappingEditDialog
          {...showMapping}
          onClose={() => setShowMapping(null)}
          onSave={(m) => saveMapping(showMapping.entity, m)}
        />
      )}
    </div>
  );
}

// ---------- Provider Card ----------

function ProviderCard({ provider, active, onConfigure, onDisconnect }) {
  const isComing = provider.coming_soon;
  return (
    <Card
      className={`p-4 border-slate-800 bg-slate-900/60 ${active ? "ring-1 ring-emerald-500/60" : ""}`}
      data-testid={`provider-card-${provider.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-white">{provider.name}</h3>
            {active && (
              <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-600/30">Bağlı</Badge>
            )}
            {isComing && (
              <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30">Yakında</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">{provider.country} • v{provider.version}</p>
          <div className="flex flex-wrap gap-1 mt-3">
            {(provider.capabilities || []).slice(0, 5).map(c => (
              <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 uppercase">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        {isComing ? (
          <Button size="sm" disabled data-testid={`provider-configure-${provider.id}`}>
            Yakında
          </Button>
        ) : active ? (
          <>
            <Button size="sm" variant="outline" onClick={onConfigure}
                    data-testid={`provider-edit-${provider.id}`}>
              Kimlik Bilgilerini Güncelle
            </Button>
            <Button size="sm" variant="destructive" onClick={onDisconnect}
                    data-testid={`provider-disconnect-${provider.id}`}>
              <Trash2 className="w-3 h-3 mr-1" />Bağlantıyı Kes
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={onConfigure}
                  data-testid={`provider-configure-${provider.id}`}>
            <Plug className="w-3 h-3 mr-1" />Bağlan
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------- Config Dialog ----------

function ConfigDialog({ provider, form, setForm, onClose, onSave, saving, existing }) {
  const fields = provider.default_config_schema?.fields || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="config-dialog">
        <DialogHeader>
          <DialogTitle>{provider.name} Bağlantısı</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <Label className="text-xs text-slate-400">{f.label}</Label>
              <Input
                type={f.type === "password" ? "password" : (f.type === "email" ? "email" : "text")}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.secret && existing?.config?.[f.key + "_set"] ? "•••••• (mevcut, boş bırak = koru)" : f.hint}
                data-testid={`config-field-${f.key}`}
              />
              {f.hint && <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>}
            </div>
          ))}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button onClick={onSave} disabled={saving} data-testid="config-save">
              {saving && <RefreshCw className="w-3 h-3 animate-spin mr-1" />}
              <Save className="w-3 h-3 mr-1" />Kaydet & Doğrula
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Status Panel ----------

function StatusPanel({ config, activeMeta, schedule, setSchedule, saveSchedule,
                      syncOne, syncingEntity, testConnection, testResult,
                      disconnect, runPreview, previewData, previewLoading }) {
  const supportedEntities = (activeMeta?.capabilities || []).filter(c =>
    ["customers", "suppliers", "products", "stock", "orders", "invoices", "payments"].includes(c),
  );
  const lastSync = config.last_sync || {};

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-slate-900/60 border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-white font-medium">{activeMeta?.name}</span>
              <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-600/30">Aktif</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Son bağlantı: {config.updated_at ? new Date(config.updated_at).toLocaleString("tr-TR") : "-"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={testConnection}
                    data-testid="test-connection-btn">
              <ArrowLeftRight className="w-3 h-3 mr-1" />Bağlantı Testi
            </Button>
            <Button size="sm" variant="outline" onClick={runPreview}
                    disabled={previewLoading}
                    data-testid="accounting-preview-btn"
                    className="border-blue-800/60 text-blue-300 hover:bg-blue-950/40">
              {previewLoading ? "Önizleniyor…" : "Verileri Önizle (Okuma)"}
            </Button>
            <Button size="sm" variant="destructive" onClick={disconnect}
                    data-testid="disconnect-btn">
              <Trash2 className="w-3 h-3 mr-1" />Bağlantıyı Kes
            </Button>
          </div>
        </div>
        {testResult && !testResult.loading && (
          <div className={`mt-3 text-sm p-2 rounded ${testResult.ok ? "bg-emerald-900/30 text-emerald-300" : "bg-red-900/30 text-red-300"}`}>
            {testResult.ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
            {testResult.message}
          </div>
        )}
        {previewData && (
          <PreviewReport data={previewData} />
        )}
      </Card>

      <Card className="p-4 bg-slate-900/60 border-slate-800">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Otomatik Senkronizasyon
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400">Sıklık</Label>
            <Select
              value={schedule.sync_interval}
              onValueChange={(v) => setSchedule({ ...schedule, sync_interval: v })}
            >
              <SelectTrigger data-testid="schedule-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(INTERVAL_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {config.next_sync_at && (
              <p className="text-[11px] text-slate-500 mt-1">
                Sonraki: {new Date(config.next_sync_at).toLocaleString("tr-TR")}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-2 block">Otomatik senkron edilecek</Label>
            <div className="grid grid-cols-2 gap-1">
              {supportedEntities.map(e => (
                <label key={e} className="flex items-center gap-2 text-xs text-slate-300 py-0.5">
                  <input type="checkbox" checked={schedule.entities.includes(e)}
                    onChange={(ev) => {
                      const set = new Set(schedule.entities);
                      ev.target.checked ? set.add(e) : set.delete(e);
                      setSchedule({ ...schedule, entities: [...set] });
                    }}
                    data-testid={`auto-entity-${e}`} />
                  {ENTITY_LABELS[e] || e}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={saveSchedule} data-testid="save-schedule-btn">
            <Save className="w-3 h-3 mr-1" />Zamanlamayı Kaydet
          </Button>
        </div>
      </Card>

      <Card className="p-4 bg-slate-900/60 border-slate-800">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <ListChecks className="w-4 h-4" /> Manuel Senkronizasyon & Durum
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {supportedEntities.map(e => {
            const meta = lastSync[e];
            return (
              <div key={e} className="p-3 rounded bg-slate-950/60 border border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white">{ENTITY_LABELS[e]}</div>
                  <Button size="sm" variant="outline" onClick={() => syncOne(e)}
                          disabled={syncingEntity === e}
                          data-testid={`sync-${e}-btn`}>
                    {syncingEntity === e ?
                      <RefreshCw className="w-3 h-3 animate-spin" /> :
                      <Play className="w-3 h-3" />}
                  </Button>
                </div>
                {meta ? (
                  <div className="text-[11px] text-slate-500 mt-2 space-y-0.5">
                    <div className="flex items-center gap-1">
                      {meta.ok ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> :
                                 <XCircle className="w-3 h-3 text-red-400" />}
                      {new Date(meta.at).toLocaleString("tr-TR")}
                    </div>
                    <div>Alınan: <b className="text-slate-300">{meta.fetched}</b> · Kayıtlı: <b className="text-slate-300">{meta.upserted}</b>
                      {meta.conflicts ? <span className="text-amber-400"> · Çakışma: {meta.conflicts}</span> : null}
                    </div>
                    {meta.errors?.[0] && (
                      <div className="text-red-400 truncate" title={meta.errors[0]}>
                        {meta.errors[0]}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-600 mt-2 italic">Henüz senkron edilmedi</p>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ---------- Mapping Panel ----------

function MappingPanel({ mapping, activeMeta, onEdit }) {
  const entities = Object.keys(mapping.defaults || {});
  if (entities.length === 0) {
    return (
      <Card className="p-6 bg-slate-900/60 border-slate-800">
        <p className="text-sm text-slate-400">
          Bu sağlayıcı için varsayılan alan eşleştirmesi henüz tanımlanmamış.
          İhtiyacınıza göre kendi eşleştirmenizi ekleyebilirsiniz.
        </p>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {entities.map(entity => {
        const defaults = mapping.defaults[entity] || {};
        const override = mapping.overrides[entity] || {};
        const effective = { ...defaults, ...override };
        return (
          <Card key={entity} className="p-4 bg-slate-900/60 border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white capitalize">
                {entity} eşleştirmesi
              </h3>
              <Button size="sm" variant="outline"
                      onClick={() => onEdit(entity, override)}
                      data-testid={`mapping-edit-${entity}`}>
                Düzenle
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-400 text-xs">Sağlayıcı Alanı</TableHead>
                  <TableHead className="text-slate-400 text-xs">ACUTO 360 Alanı</TableHead>
                  <TableHead className="text-slate-400 text-xs w-24">Kaynak</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(effective).map(([pf, ef]) => (
                  <TableRow key={pf}>
                    <TableCell className="text-slate-300 text-sm">{pf}</TableCell>
                    <TableCell className="text-slate-300 text-sm">{ef}</TableCell>
                    <TableCell>
                      {override[pf] ? (
                        <Badge className="bg-emerald-600/20 text-emerald-300">Özel</Badge>
                      ) : (
                        <Badge className="bg-slate-800 text-slate-400">Varsayılan</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        );
      })}
    </div>
  );
}

function MappingEditDialog({ entity, mapping, onClose, onSave }) {
  const [rows, setRows] = useState(
    Object.entries(mapping || {}).map(([k, v]) => ({ k, v })),
  );
  const [k, setK] = useState("");
  const [v, setV] = useState("");
  const save = () => {
    const out = {};
    rows.forEach(r => { if (r.k) out[r.k] = r.v; });
    onSave(out);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mapping-dialog">
        <DialogHeader>
          <DialogTitle>{entity} — Alan Eşleştirme</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={r.k} onChange={(e) => {
                const copy = [...rows]; copy[i] = { ...copy[i], k: e.target.value };
                setRows(copy);
              }} placeholder="Sağlayıcı alanı" />
              <ArrowLeftRight className="w-3 h-3 text-slate-500" />
              <Input value={r.v} onChange={(e) => {
                const copy = [...rows]; copy[i] = { ...copy[i], v: e.target.value };
                setRows(copy);
              }} placeholder="ERP alanı" />
              <Button size="sm" variant="ghost" onClick={() => {
                setRows(rows.filter((_, idx) => idx !== i));
              }}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
            <Input value={k} onChange={(e) => setK(e.target.value)} placeholder="Yeni sağlayıcı alanı" />
            <ArrowLeftRight className="w-3 h-3 text-slate-500" />
            <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="ERP alanı" />
            <Button size="sm" onClick={() => {
              if (k) { setRows([...rows, { k, v }]); setK(""); setV(""); }
            }}>Ekle</Button>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} data-testid="mapping-save">
            <Save className="w-3 h-3 mr-1" />Kaydet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Logs Panel ----------

function LogsPanel({ logs, queue, onRetry }) {
  return (
    <div className="space-y-4">
      {queue.length > 0 && (
        <Card className="p-4 bg-slate-900/60 border-amber-800/50">
          <h3 className="text-sm font-semibold text-amber-300 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Bekleyen / Başarısız İşlemler
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Zaman</TableHead>
                <TableHead className="text-xs">Veri</TableHead>
                <TableHead className="text-xs">Yön</TableHead>
                <TableHead className="text-xs">Deneme</TableHead>
                <TableHead className="text-xs">Sebep</TableHead>
                <TableHead className="text-xs w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.map(q => (
                <TableRow key={q.id}>
                  <TableCell className="text-xs text-slate-400">
                    {q.created_at ? new Date(q.created_at).toLocaleString("tr-TR") : "-"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-300 capitalize">{q.entity}</TableCell>
                  <TableCell className="text-xs">{q.direction}</TableCell>
                  <TableCell className="text-xs">{q.attempt || 1}</TableCell>
                  <TableCell className="text-xs text-red-300 truncate max-w-xs" title={q.reason}>
                    {q.reason?.slice(0, 60)}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => onRetry(q.id)}
                            data-testid={`retry-${q.id}`}>
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="p-4 bg-slate-900/60 border-slate-800">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" /> Son 50 Senkronizasyon
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Zaman</TableHead>
              <TableHead className="text-xs">Sağlayıcı</TableHead>
              <TableHead className="text-xs">Veri</TableHead>
              <TableHead className="text-xs">Sonuç</TableHead>
              <TableHead className="text-xs">Alınan</TableHead>
              <TableHead className="text-xs">Kayıtlı</TableHead>
              <TableHead className="text-xs">Süre</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-6 text-sm">
                  Henüz log kaydı yok
                </TableCell>
              </TableRow>
            )}
            {logs.map(l => (
              <TableRow key={l.id}>
                <TableCell className="text-xs text-slate-400">
                  {new Date(l.created_at).toLocaleString("tr-TR")}
                </TableCell>
                <TableCell className="text-xs text-slate-300">{l.provider_id}</TableCell>
                <TableCell className="text-xs capitalize">{l.entity}</TableCell>
                <TableCell className="text-xs">
                  {l.ok ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> OK
                    </span>
                  ) : (
                    <span className="text-red-400 flex items-center gap-1"
                          title={l.errors?.[0]}>
                      <XCircle className="w-3 h-3" /> HATA
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs">{l.fetched}</TableCell>
                <TableCell className="text-xs">{l.upserted}</TableCell>
                <TableCell className="text-xs text-slate-500">{l.duration_ms}ms</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


// ---------- Read-Only Preview Report (Bizim Hesap vb.) ----------
function PreviewReport({ data }) {
  const c = data?.counts || {};
  const whs = data?.warehouses || [];
  const samples = data?.samples || {};
  const errors = data?.errors || [];
  return (
    <div className="mt-4 space-y-3" data-testid="accounting-preview-report">
      <div className="rounded-sm border border-blue-800/60 bg-blue-950/20 p-3 text-xs text-blue-200">
        <strong>Salt-okuma önizleme.</strong> Aşağıdaki veriler yalnızca sağlayıcıdan çekilip gösterildi;
        ERP veritabanına <em>hiçbir kayıt yazılmadı, güncellenmedi veya silinmedi</em>.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "Depo Sayısı", val: c.warehouses ?? 0, testId: "prev-count-warehouses" },
          { label: "Müşteri Sayısı", val: c.customers ?? 0, testId: "prev-count-customers" },
          { label: "Ürün Sayısı", val: c.products ?? 0, testId: "prev-count-products" },
          { label: "Stok Kart Sayısı", val: c.stock ?? 0, testId: "prev-count-stock" },
        ].map((it) => (
          <div key={it.label} className="rounded-sm border border-slate-800 bg-slate-900/50 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{it.label}</div>
            <div className="text-lg font-bold text-emerald-300 font-mono" data-testid={it.testId}>{it.val}</div>
          </div>
        ))}
      </div>

      {whs.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Depolar ({whs.length})</div>
          <div className="rounded-sm border border-slate-800 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/60">
                <tr>
                  <th className="text-left px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">ID</th>
                  <th className="text-left px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Depo Adı</th>
                </tr>
              </thead>
              <tbody data-testid="preview-warehouses-list">
                {whs.map((w) => (
                  <tr key={w.id} className="border-t border-slate-800">
                    <td className="px-2 py-1 text-slate-500 font-mono">{w.id}</td>
                    <td className="px-2 py-1 text-slate-100">{w.name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(samples.customers?.length > 0 || samples.products?.length > 0) && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Örnek kayıtlar (ilk 5)</summary>
          {samples.customers?.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Müşteri örnekleri</div>
              <ul className="space-y-0.5 font-mono text-slate-300">
                {samples.customers.map((c2) => (
                  <li key={c2.id}>#{c2.id} — {c2.name} {c2.tax_no ? `(VKN: ${c2.tax_no})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {samples.products?.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Ürün örnekleri</div>
              <ul className="space-y-0.5 font-mono text-slate-300">
                {samples.products.map((p) => (
                  <li key={p.id}>#{p.id} — {p.name} {p.sku ? `[${p.sku}]` : ""} — ₺{p.price}</li>
                ))}
              </ul>
            </div>
          )}
        </details>
      )}

      {errors.length > 0 && (
        <div className="rounded-sm border border-rose-800/60 bg-rose-950/30 p-2 text-xs text-rose-200">
          <div className="font-semibold mb-1">Uyarılar / API hataları</div>
          <ul className="list-disc list-inside font-mono">
            {errors.map((e, i) => (
              <li key={i}>{e.stage}: {e.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
